import { Card, Grid, Menu, MenuItem } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import useGetJobReadinessDashboard from "hooks/jobs/useGetJobReadinessDashboard";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import dayjs from "dayjs";
import DispatchChecklistModal from "modules/jobs/DispatchChecklistModal";
import DispatchReadinessTable from "modules/jobs/DispatchReadinessTable";
import WorkOrderModal from "modules/jobs/WorkOrder/WorkOrderModal";
import {
  CSSProperties,
  ChangeEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { JobReadinessDailyTotal } from "types/jobReadiness";
import {
  formatChecklistStatus,
  formatDateLabel,
  formatMoney,
  formatQty,
  groupBySource,
} from "modules/jobs/dispatchReadinessTableUtils";

const MAX_RANGE_DAYS = 7;

const getRangeSize = (startDate: string, endDate: string) =>
  dayjs(endDate).diff(dayjs(startDate), "day") + 1;

const getPlantDailyNeeds = (dailyTotals: JobReadinessDailyTotal[]) => {
  const plants = new Map<
    string,
    Map<string, Map<string, { quantity: number; trucks: number; unitAbbr: string }>>
  >();

  dailyTotals.forEach((day) => {
    day.materialRows.forEach((line) => {
      const plantName = line.sourceName?.trim();
      if (!plantName) {
        return;
      }

      if (!plants.has(plantName)) {
        plants.set(plantName, new Map());
      }

      const dayMap = plants.get(plantName)!;
      if (!dayMap.has(day.scheduledDate)) {
        dayMap.set(day.scheduledDate, new Map());
      }

      const materialKey = `${line.materialName}__${line.unitAbbr}`;
      const materialMap = dayMap.get(day.scheduledDate)!;
      const existing = materialMap.get(materialKey) ?? {
        quantity: 0,
        trucks: 0,
        unitAbbr: line.unitAbbr,
      };

      materialMap.set(materialKey, {
        quantity: existing.quantity + (line.quantity || 0),
        trucks: existing.trucks + (line.trucks || 0),
        unitAbbr: line.unitAbbr,
      });
    });
  });

  return Array.from(plants.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([plantName, dayMap]) => ({
      plantName,
      days: Array.from(dayMap.entries())
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([scheduledDate, materialMap]) => ({
          scheduledDate,
          materials: Array.from(materialMap.entries())
            .map(([materialKey, totals]) => ({
              materialName: materialKey.split("__")[0],
              quantity: totals.quantity,
              trucks: totals.trucks,
              unitAbbr: totals.unitAbbr,
            }))
            .sort((left, right) => left.materialName.localeCompare(right.materialName)),
        })),
    }));
};

const escapeCsvValue = (value: string | number | boolean | null | undefined) => {
  const normalized = value == null ? "" : String(value);
  return `"${normalized.replace(/"/g, '""')}"`;
};

const escapeHtml = (value: string | number | boolean | null | undefined) => {
  const normalized = value == null ? "" : String(value);
  return normalized
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

function KpiCard({
  label,
  value,
  subtext,
  sx,
}: {
  label: string;
  value: string | number;
  subtext?: string;
  sx?: SxProps<Theme>;
}) {
  return (
    <Card sx={sx}>
      <MDBox p={2}>
        <MDTypography variant="caption" color="text" textTransform="uppercase">
          {label}
        </MDTypography>
        <MDTypography variant="h5" lineHeight={1.2}>
          {value}
        </MDTypography>
        {subtext ? (
          <MDTypography variant="caption" color="text">
            {subtext}
          </MDTypography>
        ) : null}
      </MDBox>
    </Card>
  );
}

export default function DispatchPage() {
  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dateError, setDateError] = useState<string | null>(null);
  const [didInitialLoad, setDidInitialLoad] = useState(false);
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChecklistPhaseId, setSelectedChecklistPhaseId] = useState<string | null>(null);
  const [selectedWorkOrderPhaseId, setSelectedWorkOrderPhaseId] = useState<string | null>(null);

  const { getJobReadinessDashboard, readinessData, loading, error } = useGetJobReadinessDashboard();
  const allJobsConfirmed =
    readinessData.summary.jobsConfirmedWithClient >= readinessData.summary.jobsScheduled;

  const handleExport = useCallback(() => {
    const headers = [
      "Scheduled Date",
      "Job External ID",
      "Job Name",
      "Job Phase ID",
      "Confirmed With Client",
      "Checklist Outstanding",
      "Checklist Total",
      "Revenue Scheduled",
      "Profit Scheduled",
      "Material Source",
      "Material",
      "Quantity",
      "Unit",
      "Trucks",
      "Has Plant",
    ];

    const rows = readinessData.jobs.flatMap((job) => {
      if (job.materialLines.length === 0) {
        return [
          [
            job.scheduledDate,
            job.jobExternalId || "",
            job.jobName || "",
            job.jobPhaseId,
            job.confirmedWithClient ? "Yes" : "No",
            job.checklistOutstanding,
            job.checklistTotal,
            job.revenueScheduled,
            job.profitScheduled,
            "",
            "",
            "",
            "",
            "",
            "",
          ],
        ];
      }

      return job.materialLines.map((line) => [
        job.scheduledDate,
        job.jobExternalId || "",
        job.jobName || "",
        job.jobPhaseId,
        job.confirmedWithClient ? "Yes" : "No",
        job.checklistOutstanding,
        job.checklistTotal,
        job.revenueScheduled,
        job.profitScheduled,
        line.sourceName,
        line.materialName,
        line.quantity,
        line.unitAbbr,
        line.trucks,
        line.hasPlant ? "Yes" : "No",
      ]);
    });

    const csv = [headers, ...rows].map((row) => row.map(escapeCsvValue).join(",")).join("\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `dispatch-readiness-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }, [endDate, readinessData.jobs, startDate]);

  const handleExportPdf = useCallback(() => {
    const generatedAt = dayjs().format("MMM D, YYYY h:mm A");
    const rangeLabel = `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`;
    const jobsRowsHtml =
      readinessData.jobs.length === 0
        ? `
          <tr>
            <td colspan="6">No scheduled jobs found for the selected range.</td>
          </tr>
        `
        : readinessData.jobs
            .map((job) => {
              const materialPlan =
                job.materialLines.length === 0
                  ? "No material lines"
                  : Object.entries(groupBySource(job.materialLines))
                      .map(([source, lines]) => {
                        const lineRows = lines
                          .map(
                            (line) =>
                              `${escapeHtml(line.materialName)} | ${escapeHtml(
                                formatQty(line.quantity)
                              )} ${escapeHtml(line.unitAbbr)} | ${escapeHtml(line.trucks)}`
                          )
                          .join("<br />");

                        return `<div><strong>${escapeHtml(source)}</strong><br />${lineRows}</div>`;
                      })
                      .join("<br />");

              return `
                <tr>
                  <td>
                    <strong>${escapeHtml(
                      job.jobExternalId || `Phase ${job.jobPhaseId}`
                    )}</strong><br />
                    ${escapeHtml(job.jobName || "Unnamed job")}
                  </td>
                  <td>${escapeHtml(
                    formatChecklistStatus(job.checklistTotal, job.checklistOutstanding)
                  )}</td>
                  <td>${job.confirmedWithClient ? "Yes" : "No"}</td>
                  <td>${materialPlan}</td>
                  <td class="num">${escapeHtml(formatMoney(job.revenueScheduled))}</td>
                  <td class="num">${escapeHtml(formatMoney(job.profitScheduled))}</td>
                </tr>
              `;
            })
            .join("");

    const totalsRowsHtml =
      readinessData.dailyTotals.length === 0
        ? `
          <tr>
            <td colspan="5">No daily totals found.</td>
          </tr>
        `
        : readinessData.dailyTotals
            .flatMap((day) =>
              day.materialRows.map(
                (line) => `
                  <tr>
                    <td>${escapeHtml(formatDateLabel(day.scheduledDate))}</td>
                    <td>${escapeHtml(line.sourceName)}</td>
                    <td>${escapeHtml(line.materialName)}</td>
                    <td class="num">${escapeHtml(formatQty(line.quantity))} ${escapeHtml(
                  line.unitAbbr
                )}</td>
                    <td class="num">${escapeHtml(line.trucks)}</td>
                  </tr>
                `
              )
            )
            .join("");

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Dispatch Summary ${escapeHtml(startDate)} to ${escapeHtml(endDate)}</title>
          <style>
            :root {
              --text: #1f2d3d;
              --muted: #5b6b7c;
              --line: #d9e1ea;
              --soft: #f5f8fc;
              --warn: #8f5a00;
              --warn-bg: #fff7e0;
            }
            * { box-sizing: border-box; }
            body { margin: 0; font-family: "Avenir Next", "Segoe UI", sans-serif; color: var(--text); background: #fff; }
            .page { padding: 18px 22px; }
            .head { display: grid; grid-template-columns: 1.3fr 1fr; gap: 18px; border-bottom: 1px solid var(--line); padding-bottom: 14px; }
            h1 { margin: 0; font-size: 24px; }
            .sub { margin: 6px 0 0; color: var(--muted); font-size: 13px; }
            .meta { border: 1px solid var(--line); background: var(--soft); border-radius: 10px; padding: 10px 12px; font-size: 12px; }
            .meta-row { display: flex; justify-content: space-between; margin-bottom: 6px; }
            .meta-row:last-child { margin-bottom: 0; }
            .kpi-row { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; margin-top: 14px; }
            .kpi { border: 1px solid var(--line); border-radius: 10px; background: #fff; padding: 8px; min-width: 0; }
            .kpi .label { font-size: 10px; color: var(--muted); line-height: 1.2; }
            .kpi .value { margin-top: 5px; font-size: 13px; font-weight: 700; line-height: 1.2; word-break: break-word; overflow-wrap: anywhere; }
            .section { margin-top: 14px; }
            .section h2 { margin: 0 0 10px; font-size: 15px; }
            .alert { border: 1px solid #f2d39f; background: var(--warn-bg); border-radius: 10px; padding: 10px; font-size: 12px; color: var(--warn); }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid var(--line); padding: 7px 8px; font-size: 12px; text-align: left; vertical-align: top; }
            th { background: var(--soft); }
            .num { text-align: right; font-variant-numeric: tabular-nums; }
            @media print {
              @page { size: auto; margin: 10mm; }
            }
          </style>
          <script>
            window.addEventListener("load", function () {
              setTimeout(function () {
                window.print();
              }, 250);
            });
          </script>
        </head>
        <body>
          <main class="page">
            <section class="head">
              <div>
                <h1>Daily Dispatch Summary</h1>
                <p class="sub">Schedule readiness export for operations, dispatch, and plant coordination.</p>
              </div>
              <div class="meta">
                <div class="meta-row"><strong>Range</strong><span>${escapeHtml(
                  rangeLabel
                )}</span></div>
                <div class="meta-row"><strong>Generated</strong><span>${escapeHtml(
                  generatedAt
                )}</span></div>
                <div class="meta-row"><strong>Jobs</strong><span>${escapeHtml(
                  readinessData.summary.jobsScheduled
                )}</span></div>
                <div class="meta-row"><strong>Confirmed</strong><span>${escapeHtml(
                  readinessData.summary.jobsConfirmedWithClient
                )} / ${escapeHtml(readinessData.summary.jobsScheduled)}</span></div>
              </div>
            </section>

            <section class="kpi-row">
              <article class="kpi"><div class="label">Jobs Scheduled</div><div class="value">${escapeHtml(
                readinessData.summary.jobsScheduled
              )}</div></article>
              <article class="kpi"><div class="label">Confirmed w/ Client</div><div class="value">${escapeHtml(
                readinessData.summary.jobsConfirmedWithClient
              )} / ${escapeHtml(readinessData.summary.jobsScheduled)}</div></article>
              <article class="kpi"><div class="label">Revenue Scheduled</div><div class="value">${escapeHtml(
                formatMoney(readinessData.summary.revenueScheduled)
              )}</div></article>
              <article class="kpi"><div class="label">Profit Scheduled</div><div class="value">${escapeHtml(
                formatMoney(readinessData.summary.profitScheduled)
              )}</div></article>
              <article class="kpi"><div class="label">Total Qty / Trucks</div><div class="value">${escapeHtml(
                formatQty(readinessData.summary.totalQuantity)
              )} / ${escapeHtml(readinessData.summary.totalTrucks)}</div></article>
            </section>

            <section class="section">
              <h2>Dispatch Exceptions</h2>
              <div class="alert">
                ${escapeHtml(
                  readinessData.exceptions.jobsNotConfirmedWithClient
                )} jobs not confirmed with client,
                ${escapeHtml(
                  readinessData.exceptions.jobsMissingPlantAssignment
                )} jobs missing plant assignment,
                ${escapeHtml(
                  readinessData.exceptions.jobsMissingMaterialQuantity
                )} jobs missing material quantity,
                ${escapeHtml(
                  readinessData.exceptions.materialLinesWithoutTrucks
                )} material lines without trucks.
              </div>
            </section>

            <section class="section">
              <h2>Job Readiness</h2>
              <table>
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Checklist</th>
                    <th>Confirmed</th>
                    <th>Material Plan (Plant / Material / Qty / Unit / Trucks)</th>
                    <th>Revenue</th>
                    <th>Profit</th>
                  </tr>
                </thead>
                <tbody>${jobsRowsHtml}</tbody>
              </table>
            </section>

            <section class="section">
              <h2>Daily Material Totals</h2>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Plant</th>
                    <th>Material</th>
                    <th>Qty</th>
                    <th>Trucks</th>
                  </tr>
                </thead>
                <tbody>${totalsRowsHtml}</tbody>
              </table>
            </section>
          </main>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const blobUrl = window.URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, "_blank");
    if (!printWindow) {
      window.URL.revokeObjectURL(blobUrl);
      return;
    }

    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 60000);
  }, [
    endDate,
    readinessData.dailyTotals,
    readinessData.exceptions,
    readinessData.jobs,
    readinessData.summary,
    startDate,
  ]);

  const handleExportAllPlantsPdf = useCallback(() => {
    const generatedAt = dayjs().format("MMM D, YYYY h:mm A");
    const rangeLabel = `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`;
    const plantReports = getPlantDailyNeeds(readinessData.dailyTotals);

    const plantSectionsHtml =
      plantReports.length === 0
        ? `
          <section class="plant-report">
            <h1>Plant Daily Needs</h1>
            <p class="sub">No plant material totals were found for the selected range.</p>
          </section>
        `
        : plantReports
            .map((plant) => {
              const daySectionsHtml = plant.days
                .map((day) => {
                  const rowsHtml = day.materials
                    .map(
                      (material) => `
                        <tr>
                          <td>${escapeHtml(material.materialName)}</td>
                          <td class="num">${escapeHtml(formatQty(material.quantity))}</td>
                          <td>${escapeHtml(material.unitAbbr)}</td>
                          <td class="num">${escapeHtml(material.trucks)}</td>
                        </tr>
                      `
                    )
                    .join("");

                  const dayTotalQty = day.materials.reduce((sum, material) => sum + material.quantity, 0);
                  const dayTotalTrucks = day.materials.reduce(
                    (sum, material) => sum + material.trucks,
                    0
                  );

                  return `
                    <section class="day-block">
                      <div class="day-head">
                        <h2>${escapeHtml(formatDateLabel(day.scheduledDate))}</h2>
                        <div class="day-totals">
                          <span>Total Qty: ${escapeHtml(formatQty(dayTotalQty))}</span>
                          <span>Total Trucks: ${escapeHtml(dayTotalTrucks)}</span>
                        </div>
                      </div>
                      <table class="plant-needs-table">
                        <colgroup>
                          <col class="col-material" />
                          <col class="col-qty" />
                          <col class="col-unit" />
                          <col class="col-trucks" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>Material</th>
                            <th class="num">Total Qty</th>
                            <th>Unit</th>
                            <th class="num">Trucks</th>
                          </tr>
                        </thead>
                        <tbody>${rowsHtml}</tbody>
                      </table>
                    </section>
                  `;
                })
                .join("");

              return `
                <section class="plant-report">
                  <header class="head">
                    <div>
                      <h1>${escapeHtml(plant.plantName)}</h1>
                      <p class="sub">Daily material needs for the selected dispatch range.</p>
                    </div>
                    <div class="meta">
                      <div class="meta-row"><strong>Range</strong><span>${escapeHtml(
                        rangeLabel
                      )}</span></div>
                      <div class="meta-row"><strong>Generated</strong><span>${escapeHtml(
                        generatedAt
                      )}</span></div>
                      <div class="meta-row"><strong>Days</strong><span>${escapeHtml(
                        plant.days.length
                      )}</span></div>
                    </div>
                  </header>
                  ${daySectionsHtml}
                </section>
              `;
            })
            .join("");

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Plant Daily Needs ${escapeHtml(startDate)} to ${escapeHtml(endDate)}</title>
          <style>
            :root {
              --text: #1f2d3d;
              --muted: #5b6b7c;
              --line: #d9e1ea;
              --soft: #f5f8fc;
            }
            * { box-sizing: border-box; }
            body { margin: 0; font-family: "Avenir Next", "Segoe UI", sans-serif; color: var(--text); background: #fff; }
            .plant-report { padding: 22px 24px; page-break-after: always; }
            .plant-report:last-child { page-break-after: auto; }
            .head { display: grid; grid-template-columns: 1.4fr 1fr; gap: 18px; border-bottom: 1px solid var(--line); padding-bottom: 14px; }
            h1 { margin: 0; font-size: 26px; }
            h2 { margin: 0; font-size: 16px; }
            .sub { margin: 6px 0 0; color: var(--muted); font-size: 13px; }
            .meta { border: 1px solid var(--line); background: var(--soft); border-radius: 10px; padding: 10px 12px; font-size: 12px; }
            .meta-row { display: flex; justify-content: space-between; margin-bottom: 6px; }
            .meta-row:last-child { margin-bottom: 0; }
            .day-block { margin-top: 18px; }
            .day-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; }
            .day-totals { display: flex; gap: 16px; color: var(--muted); font-size: 12px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid var(--line); padding: 8px 9px; font-size: 12px; text-align: left; vertical-align: top; }
            th { background: var(--soft); }
            .plant-needs-table { table-layout: fixed; }
            .col-material { width: 54%; }
            .col-qty { width: 18%; }
            .col-unit { width: 10%; }
            .col-trucks { width: 18%; }
            .plant-needs-table td:first-child,
            .plant-needs-table th:first-child {
              word-break: break-word;
              overflow-wrap: anywhere;
            }
            .num { text-align: right; font-variant-numeric: tabular-nums; }
            @media print { @page { size: auto; margin: 10mm; } }
          </style>
          <script>
            window.addEventListener("load", function () {
              setTimeout(function () {
                window.print();
              }, 250);
            });
          </script>
        </head>
        <body>${plantSectionsHtml}</body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const blobUrl = window.URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, "_blank");
    if (!printWindow) {
      window.URL.revokeObjectURL(blobUrl);
      return;
    }

    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 60000);
  }, [endDate, readinessData.dailyTotals, startDate]);

  const handleOpenExportMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setExportMenuAnchorEl(event.currentTarget);
  }, []);

  const handleCloseExportMenu = useCallback(() => {
    setExportMenuAnchorEl(null);
  }, []);

  const handleExportCsvClick = useCallback(() => {
    handleCloseExportMenu();
    handleExport();
  }, [handleCloseExportMenu, handleExport]);

  const handleExportPdfClick = useCallback(() => {
    handleCloseExportMenu();
    handleExportPdf();
  }, [handleCloseExportMenu, handleExportPdf]);

  const handleExportAllPlantsPdfClick = useCallback(() => {
    handleCloseExportMenu();
    handleExportAllPlantsPdf();
  }, [handleCloseExportMenu, handleExportAllPlantsPdf]);

  const loadReadiness = useCallback(
    async (nextStartDate: string, nextEndDate: string) => {
      if (!nextStartDate || !nextEndDate) {
        setDateError("Start and end date are required.");
        return;
      }

      if (dayjs(nextEndDate).isBefore(dayjs(nextStartDate), "day")) {
        setDateError("End date must be on or after start date.");
        return;
      }

      const rangeSize = getRangeSize(nextStartDate, nextEndDate);
      if (rangeSize > MAX_RANGE_DAYS) {
        setDateError("Date range cannot exceed 7 days.");
        return;
      }

      setDateError(null);
      await getJobReadinessDashboard({
        startDate: nextStartDate,
        endDate: nextEndDate,
      });
    },
    [getJobReadinessDashboard]
  );

  useEffect(() => {
    if (didInitialLoad) {
      return;
    }
    setDidInitialLoad(true);
    loadReadiness(today, today);
  }, [didInitialLoad, loadReadiness, today]);

  const handleCloseChecklistModal = useCallback(() => {
    setSelectedChecklistPhaseId(null);
    loadReadiness(startDate, endDate);
  }, [endDate, loadReadiness, startDate]);

  const handleCloseWorkOrderModal = useCallback(() => {
    setSelectedWorkOrderPhaseId(null);
    loadReadiness(startDate, endDate);
  }, [endDate, loadReadiness, startDate]);

  return (
    <DashboardPage>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h4" fontWeight="medium">
                Dispatch
              </MDTypography>
              <MDTypography variant="button" color="text">
                Schedule readiness, checklist completion, material needs, and trucking demand.
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} md={3}>
                  <MDInput
                    label="Start Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setStartDate(event.target.value)
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDInput
                    label="End Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setEndDate(event.target.value)
                    }
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    fullWidth
                    onClick={() => loadReadiness(startDate, endDate)}
                    disabled={loading}
                  >
                    {loading ? "Applying..." : "Apply"}
                  </MDButton>
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    fullWidth
                    onClick={handleOpenExportMenu}
                    disabled={loading || readinessData.jobs.length === 0}
                  >
                    Export
                  </MDButton>
                  <Menu
                    anchorEl={exportMenuAnchorEl}
                    open={Boolean(exportMenuAnchorEl)}
                    onClose={handleCloseExportMenu}
                  >
                    <MenuItem onClick={handleExportCsvClick}>Export CSV</MenuItem>
                    <MenuItem onClick={handleExportPdfClick}>Export PDF</MenuItem>
                    <MenuItem onClick={handleExportAllPlantsPdfClick}>Export All Plants</MenuItem>
                  </Menu>
                </Grid>
                <Grid item xs={12} md={2}>
                  <MDTypography variant="caption" color="text">
                    Maximum range is 7 days.
                  </MDTypography>
                </Grid>
              </Grid>

              {dateError ? (
                <MDBox mt={1}>
                  <MDTypography variant="caption" color="error">
                    {dateError}
                  </MDTypography>
                </MDBox>
              ) : null}
              {error ? (
                <MDBox mt={1}>
                  <MDTypography variant="caption" color="error">
                    Failed to load dispatch readiness data.
                  </MDTypography>
                </MDBox>
              ) : null}
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <KpiCard
            label="Revenue Scheduled"
            value={formatMoney(readinessData.summary.revenueScheduled)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            label="Profit Scheduled"
            value={formatMoney(readinessData.summary.profitScheduled)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard label="Jobs Scheduled" value={readinessData.summary.jobsScheduled} />
        </Grid>
        <Grid item xs={12} md={3}>
          <KpiCard
            label="Confirmed with Client"
            value={`${readinessData.summary.jobsConfirmedWithClient}/${readinessData.summary.jobsScheduled}`}
            sx={
              allJobsConfirmed
                ? { background: "#e8f5e9", border: "1px solid #81c784" }
                : { background: "#ffebee", border: "1px solid #ef9a9a" }
            }
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6">Job Readiness</MDTypography>
            </MDBox>
            <MDBox sx={{ overflowX: "auto" }}>
              <DispatchReadinessTable
                jobs={readinessData.jobs}
                emptyText="No scheduled jobs found for the selected range."
                onOpenChecklist={setSelectedChecklistPhaseId}
                onOpenWorkOrder={setSelectedWorkOrderPhaseId}
                thStyle={thStyle}
                tdStyle={tdStyle}
              />
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={2}>
              <MDTypography variant="h6">Daily Material Totals</MDTypography>
            </MDBox>
            <MDBox px={1.5} pb={1.5} display="grid" gap={1}>
              {readinessData.dailyTotals.length === 0 ? (
                <MDTypography variant="caption" color="text">
                  No daily totals found.
                </MDTypography>
              ) : (
                readinessData.dailyTotals.map((day) => {
                  const rows = day.materialRows.map((line) => ({
                    plant: line.sourceName,
                    material: line.materialName,
                    qty: line.quantity,
                    unit: line.unitAbbr,
                    trucks: line.trucks,
                  }));

                  return (
                    <Card
                      key={day.scheduledDate}
                      sx={{ boxShadow: "none", border: "1px solid #dde3ec" }}
                    >
                      <MDBox px={1.5} py={1} sx={{ borderBottom: "1px solid #dde3ec" }}>
                        <MDTypography variant="button" fontWeight="medium">
                          {formatDateLabel(day.scheduledDate)} - {formatQty(day.totalQuantity)} Qty
                          - {day.totalTrucks} Trucks
                        </MDTypography>
                      </MDBox>
                      <MDBox sx={{ overflowX: "auto" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            tableLayout: "fixed",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "28%" }} />
                            <col style={{ width: "28%" }} />
                            <col style={{ width: "14%" }} />
                            <col style={{ width: "12%" }} />
                            <col style={{ width: "18%" }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th style={thStyle}>Plant</th>
                              <th style={thStyle}>Material</th>
                              <th style={{ ...thStyle, textAlign: "right" }}>Qty</th>
                              <th style={thStyle}>Unit</th>
                              <th style={{ ...thStyle, textAlign: "right" }}>Trucks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, index) => (
                              <tr key={`${day.scheduledDate}-${index}`}>
                                <td style={tdStyle}>{row.plant}</td>
                                <td style={tdStyle}>{row.material}</td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    textAlign: "right",
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {formatQty(row.qty)}
                                </td>
                                <td style={tdStyle}>{row.unit}</td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    textAlign: "right",
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  {row.trucks}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </MDBox>
                    </Card>
                  );
                })
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>
      <DispatchChecklistModal
        open={Boolean(selectedChecklistPhaseId)}
        jobPhaseId={selectedChecklistPhaseId}
        onClose={handleCloseChecklistModal}
      />
      <WorkOrderModal
        open={Boolean(selectedWorkOrderPhaseId)}
        jobPhaseId={selectedWorkOrderPhaseId}
        onClose={handleCloseWorkOrderModal}
        hideTrigger
      />
    </DashboardPage>
  );
}

const thStyle: CSSProperties = {
  fontSize: 12,
  color: "#6b778c",
  fontWeight: 700,
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid #dde3ec",
  background: "#f9fbfd",
};

const tdStyle: CSSProperties = {
  fontSize: 12,
  color: "#253347",
  textAlign: "left",
  padding: "8px 10px",
  borderBottom: "1px solid #edf2f7",
  verticalAlign: "top",
};
